import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Input, message, Modal, Popover, Row, Spin, Upload } from 'antd';
import debounce from 'lodash.debounce';
import api from '@/api/businessAdminUser';
import Pagination from '@/components/pagination';
import { searchIcon } from '@/constants/static';
import type { UploadFile } from 'antd/es/upload/interface';

interface ListParams {
    pageNum: number;
    pageSize: number;
    keyword: string;
}

interface BusinessAdminUserItem {
    businessAdminUserId: string;
    email: string;
    mobileNumber?: string;
    mobile?: string;
    street: string;
    state: string;
    pincode: string;
    country: string;
    addressStreet?: string;
    addressState?: string;
    addressPincode?: string;
    addressCountry?: string;
    companyName: string;
    addressLine1: string;
    addressLine2: string;
    companyAddressLine1?: string;
    companyAddressLine2?: string;
    companyLogoUrl: string;
}

type FormType = Omit<BusinessAdminUserItem, 'businessAdminUserId'>;

const defaultFormValues: FormType = {
    email: '',
    mobileNumber: '',
    street: '',
    state: '',
    pincode: '',
    country: '',
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    companyLogoUrl: '',
};

const BusinessAdminUser = () => {
    const [loading, setLoading] = useState(false);
    const [totalRecord, setTotalRecord] = useState(0);
    const [tableData, setTableData] = useState<BusinessAdminUserItem[]>([]);
    const [listParams, setListParams] = useState<ListParams>({
        pageNum: 1,
        pageSize: 10,
        keyword: '',
    });

    const [form] = Form.useForm<FormType>();
    const [modalType, setModalType] = useState<'create' | 'edit'>('create');
    const [currentId, setCurrentId] = useState('');
    const [showFormModal, setShowFormModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState<BusinessAdminUserItem | null>(null);
    const [logoFileList, setLogoFileList] = useState<UploadFile[]>([]);

    const normalizeList = (responseData: any) => {
        if (Array.isArray(responseData?.records)) {
            return {
                records: responseData.records,
                total: Number(responseData.total ?? responseData.records.length ?? 0),
            };
        }
        if (Array.isArray(responseData?.list)) {
            return {
                records: responseData.list,
                total: Number(responseData.total ?? responseData.list.length ?? 0),
            };
        }
        if (Array.isArray(responseData)) {
            return {
                records: responseData,
                total: Number(responseData.length ?? 0),
            };
        }
        return {
            records: [],
            total: 0,
        };
    };

    const getMobileNumber = (data: { mobile?: string; mobileNumber?: string }) =>
        data.mobileNumber || data.mobile || '';
    const getStreet = (data: BusinessAdminUserItem) => data.street || data.addressStreet || '';
    const getState = (data: BusinessAdminUserItem) => data.state || data.addressState || '';
    const getPincode = (data: BusinessAdminUserItem) => data.pincode || data.addressPincode || '';
    const getCountry = (data: BusinessAdminUserItem) => data.country || data.addressCountry || '';
    const getAddressLine1 = (data: BusinessAdminUserItem) => data.addressLine1 || data.companyAddressLine1 || '';
    const getAddressLine2 = (data: BusinessAdminUserItem) => data.addressLine2 || data.companyAddressLine2 || '';

    const getList = async (params: ListParams) => {
        setLoading(true);
        try {
            const res = await api.getBusinessAdminUserList(params);
            const normalized = normalizeList(res.data);
            setTableData(normalized.records || []);
            setTotalRecord(normalized.total || 0);
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getList(listParams);
    }, [listParams]);

    const debouncedKeyword = useCallback(
        debounce((value: string) => {
            setListParams(state => ({ ...state, keyword: value, pageNum: 1 }));
        }, 500),
        []
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        debouncedKeyword(event.target.value);
    };

    const handlePageChange = (pageNo: number) => {
        setListParams(state => ({ ...state, pageNum: pageNo }));
    };

    const openCreateModal = () => {
        setModalType('create');
        setCurrentId('');
        form.setFieldsValue(defaultFormValues);
        setLogoFileList([]);
        setShowFormModal(true);
    };

    const openEditModal = async (businessAdminUserId: string) => {
        setModalType('edit');
        setCurrentId(businessAdminUserId);
        setShowFormModal(true);
        setDetailLoading(true);
        try {
            const res = await api.getBusinessAdminUserDetail(businessAdminUserId);
            const row = res.data || {};
            form.setFieldsValue({
                email: row.email || '',
                mobileNumber: getMobileNumber(row),
                street: row.street || row.addressStreet || '',
                state: row.state || row.addressState || '',
                pincode: row.pincode || row.addressPincode || '',
                country: row.country || row.addressCountry || '',
                companyName: row.companyName || '',
                addressLine1: row.addressLine1 || row.companyAddressLine1 || '',
                addressLine2: row.addressLine2 || row.companyAddressLine2 || '',
                companyLogoUrl: row.companyLogoUrl || '',
            });
            setLogoFileList([]);
        } catch (error: any) {
            message.error(error);
        } finally {
            setDetailLoading(false);
        }
    };

    const openDetailModal = async (businessAdminUserId: string) => {
        setShowDetailModal(true);
        setDetailLoading(true);
        try {
            const res = await api.getBusinessAdminUserDetail(businessAdminUserId);
            setDetailData(res.data || null);
        } catch (error: any) {
            message.error(error);
        } finally {
            setDetailLoading(false);
        }
    };

    const submitForm = async () => {
        try {
            const values = await form.validateFields();
            const formData = new FormData();
            const data = {
                email: values.email,
                mobileNumber: values.mobileNumber,
                street: values.street,
                state: values.state,
                pincode: values.pincode,
                country: values.country,
                companyName: values.companyName,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
            };
            formData.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));
            if (logoFileList.length && logoFileList[0].originFileObj) {
                formData.append('companyLogoFile', logoFileList[0].originFileObj);
            }
            if (modalType === 'create') {
                await api.createBusinessAdminUser(formData);
                message.success('Business admin user created');
            } else {
                await api.updateBusinessAdminUser(currentId, formData);
                message.success('Business admin user updated');
            }
            setShowFormModal(false);
            form.resetFields();
            setLogoFileList([]);
            getList(listParams);
        } catch (error: any) {
            if (!error?.errorFields) {
                message.error(error);
            }
        }
    };

    const confirmDelete = async () => {
        try {
            await api.deleteBusinessAdminUser(currentId);
            message.success('Business admin user deleted');
            setShowDeleteModal(false);
            setCurrentId('');
            const targetPage =
                tableData.length === 1 && listParams.pageNum > 1 ? listParams.pageNum - 1 : listParams.pageNum;
            setListParams(state => ({ ...state, pageNum: targetPage }));
        } catch (error: any) {
            message.error(error);
        }
    };

    return (
        <>
            <div className="page-main account">
                <div className="page-title">Business Admin User</div>
                <div className="select-bar">
                    <div className="select-bar-left">
                        <Input
                            className="search-input"
                            placeholder="Search by email or mobile"
                            onChange={handleSearch}
                            prefix={searchIcon}
                        />
                    </div>
                    <Button
                        type="primary"
                        size="small"
                        style={{ height: 46, width: 180, borderRadius: 8, justifySelf: 'flex-end' }}
                        onClick={openCreateModal}
                    >
                        Add Business Admin
                    </Button>
                </div>

                {loading ? (
                    <div className="data_loading flex-center">
                        <Spin />
                    </div>
                ) : (
                    <div className="table">
                        {totalRecord ? (
                            <>
                                <div className="table-th">
                                    <Row>
                                        <Col span={2}>Logo</Col>
                                        <Col span={3}>Email</Col>
                                        <Col span={3}>Mobile</Col>
                                        <Col span={4}>Company</Col>
                                        <Col span={5}>Address</Col>
                                        <Col span={6}>Company Address</Col>
                                        <Col span={1}></Col>
                                    </Row>
                                </div>
                                {tableData.map(item => (
                                    <Row className="table-tr" key={item.businessAdminUserId}>
                                        <Col span={2}>
                                            {item.companyLogoUrl ? (
                                                <img
                                                    src={item.companyLogoUrl}
                                                    alt="company-logo"
                                                    style={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </Col>
                                        <Col span={3}>{item.email || '-'}</Col>
                                        <Col span={3}>
                                            {getMobileNumber(item)
                                                ? window.utils.formatMobileNumber(getMobileNumber(item))
                                                : '-'}
                                        </Col>
                                        <Col span={4}>{item.companyName || '-'}</Col>
                                        <Col span={5}>
                                            {[getStreet(item), getState(item), getPincode(item), getCountry(item)]
                                                .filter(Boolean)
                                                .join(', ') || '-'}
                                        </Col>
                                        <Col span={6}>
                                            {[getAddressLine1(item), getAddressLine2(item)]
                                                .filter(Boolean)
                                                .join(', ') || '-'}
                                        </Col>
                                        <Col span={1}>
                                            <Popover
                                                placement="bottomRight"
                                                content={
                                                    <div className="view-more">
                                                        <div onClick={() => openDetailModal(item.businessAdminUserId)}>
                                                            Details
                                                        </div>
                                                        <div onClick={() => openEditModal(item.businessAdminUserId)}>
                                                            Edit
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                setCurrentId(item.businessAdminUserId);
                                                                setShowDeleteModal(true);
                                                            }}
                                                        >
                                                            Delete
                                                        </div>
                                                    </div>
                                                }
                                            >
                                                <img
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                        cursor: 'pointer',
                                                        textAlign: 'right',
                                                    }}
                                                    src={require('@/assets/images/view-more.png')}
                                                    alt=""
                                                />
                                            </Popover>
                                        </Col>
                                    </Row>
                                ))}
                                <Pagination
                                    total={totalRecord}
                                    pageChange={handlePageChange}
                                    pageNo={listParams.pageNum}
                                />
                            </>
                        ) : (
                            <div className="no-data flex-center">No data</div>
                        )}
                    </div>
                )}
            </div>

            <Modal
                width={760}
                centered
                maskClosable={false}
                title={modalType === 'create' ? 'Create Business Admin User' : 'Edit Business Admin User'}
                open={showFormModal}
                onCancel={() => {
                    setShowFormModal(false);
                    setLogoFileList([]);
                }}
                footer={null}
            >
                <Spin spinning={detailLoading}>
                    <Form form={form} layout="vertical" className="add-Categorie-form">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[
                                        { required: true, message: 'Please enter email' },
                                        { type: 'email', message: 'Please enter valid email' },
                                    ]}
                                >
                                    <Input placeholder="Enter email" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="mobileNumber"
                                    label="Mobile Number"
                                    rules={[
                                        { required: true, message: 'Please enter mobile number' },
                                        { pattern: /^\d{6,15}$/, message: 'Please enter valid mobile number' },
                                    ]}
                                >
                                    <Input placeholder="Enter mobile number" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="companyName"
                                    label="Company Name"
                                    rules={[{ required: true, message: 'Please enter company name' }]}
                                >
                                    <Input placeholder="Enter company name" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Company Logo File" extra="Upload logo image (png, jpg, jpeg)">
                                    <Upload
                                        accept=".png,.jpg,.jpeg"
                                        maxCount={1}
                                        beforeUpload={() => false}
                                        fileList={logoFileList}
                                        onChange={({ fileList }) => setLogoFileList(fileList)}
                                    >
                                        <Button>Choose File</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="street"
                                    label="Street"
                                    rules={[{ required: true, message: 'Please enter street' }]}
                                >
                                    <Input placeholder="Enter street" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="state"
                                    label="State"
                                    rules={[{ required: true, message: 'Please enter state' }]}
                                >
                                    <Input placeholder="Enter state" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="pincode"
                                    label="Pincode"
                                    rules={[{ required: true, message: 'Please enter pincode' }]}
                                >
                                    <Input placeholder="Enter pincode" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="country"
                                    label="Country"
                                    rules={[{ required: true, message: 'Please enter country' }]}
                                >
                                    <Input placeholder="Enter country" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="addressLine1" label="Address Line 1">
                                    <Input placeholder="Enter address line 1" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="addressLine2" label="Address Line 2">
                                    <Input placeholder="Enter address line 2" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Button className="submit-btn cancel-btn" onClick={() => setShowFormModal(false)}>
                                    Cancel
                                </Button>
                            </Col>
                            <Col span={12}>
                                <Button type="primary" className="submit-btn" onClick={submitForm}>
                                    {modalType === 'create' ? 'Create' : 'Update'}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Spin>
            </Modal>

            <Modal width={560} centered maskClosable={false} open={showDeleteModal} closeIcon={<></>} footer={null}>
                <div className="modal-title">Delete account</div>
                <div className="modal-tip tip-font-20">Are you sure you want to delete this business admin user?</div>
                <div className="flex-center" style={{ marginTop: 30, columnGap: 20 }}>
                    <Button className="submit-btn" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button className="submit-btn" type="primary" onClick={confirmDelete}>
                        Yes, delete
                    </Button>
                </div>
            </Modal>

            <Modal
                width={760}
                centered
                maskClosable={false}
                title="Business Admin User Details"
                open={showDetailModal}
                onCancel={() => setShowDetailModal(false)}
                footer={null}
            >
                <Spin spinning={detailLoading}>
                    {detailData ? (
                        <div className="content-card" style={{ marginTop: 0 }}>
                            <div className="card-item-label">
                                Email <span>{detailData.email || '-'}</span>
                            </div>
                            <div className="card-item-label">
                                Mobile{' '}
                                <span>
                                    {getMobileNumber(detailData)
                                        ? window.utils.formatMobileNumber(getMobileNumber(detailData))
                                        : '-'}
                                </span>
                            </div>
                            <div className="card-item-label">
                                Company <span>{detailData.companyName || '-'}</span>
                            </div>
                            <div className="card-item-label">
                                Address
                                <span>
                                    {[
                                        getStreet(detailData),
                                        getState(detailData),
                                        getPincode(detailData),
                                        getCountry(detailData),
                                    ]
                                        .filter(Boolean)
                                        .join(', ') || '-'}
                                </span>
                            </div>
                            <div className="card-item-label">
                                Company Address
                                <span>
                                    {[getAddressLine1(detailData), getAddressLine2(detailData)]
                                        .filter(Boolean)
                                        .join(', ') || '-'}
                                </span>
                            </div>
                            <div className="card-item-label">
                                Company Logo URL <span>{detailData.companyLogoUrl || '-'}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="no-data flex-center">No data</div>
                    )}
                </Spin>
            </Modal>
        </>
    );
};

export default BusinessAdminUser;
